// Central mapper from backend/transport errors to safe, user-facing copy.
// Prevents raw server text (SQL, stack traces, constraint/FK names, framework
// exceptions) from leaking into toasts and inline form errors.

const TECHNICAL_HINTS =
  /(\bSQL\b|constraint|violat|exception|stack trace|null reference|ECONNREFUSED|System\.|Microsoft\.|::|\bFK\b|foreign key|duplicate key|npgsql|sequelize|at [\w.$]+\()/i;

interface ApiErrorShape {
  response?: {
    status?: number;
    data?: { message?: string; error?: string };
  };
}

/**
 * Returns a concise, user-safe message for an axios/network error.
 * A server-provided message is surfaced only when it is short and free of
 * obvious technical markers; otherwise a friendly fallback is used.
 */
export function friendlyErrorMessage(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  const err = error as ApiErrorShape;

  if (!err?.response) return 'Network error. Please check your connection and try again.';

  const status = err.response.status;
  if (status && status >= 500) return 'Server error. Please try again in a moment.';
  if (status === 401) return 'Your session has expired. Please sign in again.';
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "We couldn't find what you were looking for.";

  const raw = err.response.data?.message || err.response.data?.error;
  if (raw && typeof raw === 'string' && raw.trim().length > 0 && raw.length <= 160 && !TECHNICAL_HINTS.test(raw)) {
    return raw;
  }
  return fallback;
}
